import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import VersionBadge from '../components/common/VersionBadge';

const SA_NAV = [
  { label: 'Dashboard',      icon: 'bi-speedometer2',  path: '/sa/dashboard' },
  { label: 'Tenants',        icon: 'bi-buildings',     path: '/sa/tenants' },
  { label: 'Industries',     icon: 'bi-diagram-3',     path: '/sa/industries' },
  { label: 'Modules',        icon: 'bi-grid-3x3-gap',  path: '/sa/modules' },
  { label: 'Plans',          icon: 'bi-credit-card',   path: '/sa/plans' },
  { label: 'Notifications',  icon: 'bi-bell',          path: '/sa/notifications' },
  { label: 'Data Migration', icon: 'bi-database-up',   path: '/sa/migrate' },
  { label: 'App Version',    icon: 'bi-tag',           path: '/sa/app-version' },
];

const SEV_COLOR = { info: '#3b82f6', success: '#10b981', warning: '#f59e0b', error: '#ef4444' };
const SEV_BG    = { info: '#dbeafe', success: '#dcfce7', warning: '#fef9c3', error: '#fee2e2' };

function timeAgo(ts) {
  const d = (Date.now() - new Date(ts)) / 1000;
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

// ─── Notification Bell Dropdown ───────────────────────────────────────────────
function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/sa/notifications?limit=10');
      const list = res.data.data?.notifications || res.data.data || [];
      setItems(list);
      setUnread(list.filter(n => !n.isRead).length);
    } catch (_) {}
  }, []);

  // Poll every 30s
  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, 30000);
    return () => clearInterval(t);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    try { await api.patch(`/sa/notifications/${id}/read`); } catch (_) {}
    setItems(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
    setUnread(c => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    try { await api.patch('/sa/notifications/read-all'); } catch (_) {}
    setItems(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnread(0);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(o => !o); if (!open) fetchNotifications(); }}
        style={{ position: 'relative', width: 36, height: 36, borderRadius: 10, border: 'none', background: open ? '#ede9fe' : '#f8fafc', color: open ? '#6366f1' : '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
        title="Notifications"
      >
        <i className="bi bi-bell" style={{ fontSize: 17 }} />
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 4, right: 4, width: unread > 9 ? 16 : 14, height: 14, borderRadius: 8, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 44, right: 0, width: 360, background: '#fff', borderRadius: 14, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 2000, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
              Notifications {unread > 0 && <span style={{ background: '#ede9fe', color: '#6366f1', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700, marginLeft: 6 }}>{unread} new</span>}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {items.length === 0 && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8' }}>
                <i className="bi bi-bell-slash" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} />
                <div style={{ fontSize: 13 }}>No notifications yet</div>
              </div>
            )}
            {items.map(n => (
              <div
                key={n._id}
                onClick={() => !n.isRead && markRead(n._id)}
                style={{ padding: '11px 16px', borderBottom: '1px solid #f8fafc', background: n.isRead ? '#fff' : '#fdfcff', cursor: n.isRead ? 'default' : 'pointer', transition: 'background 0.2s' }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: SEV_BG[n.severity] || SEV_BG.info, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <i className="bi bi-bell-fill" style={{ fontSize: 13, color: SEV_COLOR[n.severity] || SEV_COLOR.info }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: n.isRead ? 500 : 700, fontSize: 12.5, color: '#0f172a', lineHeight: 1.35 }}>{n.title}</div>
                    {n.message && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</div>}
                    <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 3 }}>
                      {n.tenantName && <span className="me-2">{n.tenantName}</span>}
                      {timeAgo(n.createdAt)}
                    </div>
                  </div>
                  {!n.isRead && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#6366f1', flexShrink: 0, marginTop: 5 }} />}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
            <button
              onClick={() => { setOpen(false); navigate('/sa/notifications'); }}
              style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
            >
              View all notifications <i className="bi bi-arrow-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────
export default function SuperAdminLayout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = `${user?.firstName?.[0] || 'S'}${user?.lastName?.[0] || 'A'}`.toUpperCase();

  return (
    <div className="app-wrapper">
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1039 }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* SA Sidebar */}
      <aside className={`app-sidebar sa-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-icon">
            <i className="bi bi-shield-lock-fill" />
          </div>
          <div className="sidebar-brand-text">
            Platform Admin
            <span>Super Admin Panel</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-group-label">Navigation</div>
          {SA_NAV.map(item => (
            <div className="nav-item" key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) => `nav-link-item ${isActive ? 'active' : ''}`}
                title={collapsed ? item.label : undefined}
                onClick={() => setMobileOpen(false)}
              >
                <i className={`bi ${item.icon}`} />
                <span className="nav-label">{item.label}</span>
              </NavLink>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="nav-link-item" style={{ pointerEvents: 'none', opacity: 0.7, marginBottom: 4 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
              {initials}
            </div>
            <span className="nav-label" style={{ fontSize: 12 }}>{user?.email}</span>
          </div>
          <button className="nav-link-item w-100" onClick={logout} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            <i className="bi bi-box-arrow-left" />
            <span className="nav-label">Sign Out</span>
          </button>
          <VersionBadge collapsed={collapsed} superAdmin />
        </div>
      </aside>

      {/* Main */}
      <div className={`app-main ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <header className="app-topbar">
          <button className="topbar-toggle d-none d-lg-flex" onClick={() => setCollapsed(c => !c)}>
            <i className={`bi ${collapsed ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar'}`} />
          </button>
          <button className="topbar-toggle d-lg-none" onClick={() => setMobileOpen(o => !o)}>
            <i className="bi bi-list" style={{ fontSize: 22 }} />
          </button>
          <div className="topbar-title" style={{ color: '#0f172a' }}>
            <i className="bi bi-shield-check text-primary me-2" style={{ fontSize: 15 }} />
            Platform Administration
          </div>
          <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <NotificationBell />
            <div className="user-avatar" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }} title={user?.email}>
              {initials}
            </div>
          </div>
        </header>

        <main className="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
